"use client";

import { c as _c } from "react/compiler-runtime";
import { controlSurfaceVariants } from "@/components/ui/control-surface";
import { menuContentVariants, menuItemIndicatorClassName, menuItemVariants, menuLabelClassName, menuSeparatorClassName } from "@/components/ui/menu-surface";
import { useOrderedOverlayContainer } from "@/components/ui/ordered-overlay";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";
import cn from "twsx";

/** Use when a person picks one option from a short, fixed list. */
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = t0 => {
  const $ = _c(8);
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      placeholder: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
    $[3] = t1;
  } else {
    props = $[1];
    ref = $[2];
    t1 = $[3];
  }
  const placeholder = t1 === undefined ? "Select" : t1;
  let t2;
  if ($[4] !== placeholder || $[5] !== props || $[6] !== ref) {
    t2 = <SelectPrimitive.Value ref={ref} placeholder={placeholder} {...props} />;
    $[4] = placeholder;
    $[5] = props;
    $[6] = ref;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
SelectValue.displayName = SelectPrimitive.Value.displayName;

// md matches the 32px text field for forms; sm is Linear's 24px option-row
// trigger (white, ring .103, open adds the hover fill and a 1px border).
const selectTriggerSizes = {
  md: controlSurfaceVariants({
    size: "md"
  }),
  sm: "h-6 w-full rounded-control border border-transparent bg-bg-surface px-2 text-sm text-text-secondary outline-none shadow-tab-selected hover:bg-fill-pill-hover hover:text-text-primary focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent disabled:opacity-disabled data-[state=open]:border-border-input-hover data-[state=open]:bg-fill-pill-hover data-[state=open]:text-text-primary data-[state=open]:shadow-none"
} as const;
const SelectTrigger = t0 => {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      size: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? "md" : t1;
  const t2 = selectTriggerSizes[size];
  let t3;
  if ($[6] !== className || $[7] !== t2) {
    t3 = cn(t2, "flex items-center justify-between gap-2 text-left data-[placeholder]:text-text-placeholder [&>span]:line-clamp-1", className);
    $[6] = className;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SelectPrimitive.Icon asChild={true}><ChevronDown className="size-2.5 shrink-0 text-text-tertiary" strokeWidth={2.25} /></SelectPrimitive.Icon>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children || $[11] !== props || $[12] !== ref || $[13] !== t3) {
    t5 = <SelectPrimitive.Trigger ref={ref} className={t3} {...props}>{children}{t4}</SelectPrimitive.Trigger>;
    $[10] = children;
    $[11] = props;
    $[12] = ref;
    $[13] = t3;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
const SelectScrollUpButton = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex cursor-default items-center justify-center py-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUp className="size-3.5" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t1) {
    t3 = <SelectPrimitive.ScrollUpButton ref={ref} className={t1} {...props}>{t2}</SelectPrimitive.ScrollUpButton>;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
const SelectScrollDownButton = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex cursor-default items-center justify-center py-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDown className="size-3.5" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t1) {
    t3 = <SelectPrimitive.ScrollDownButton ref={ref} className={t1} {...props}>{t2}</SelectPrimitive.ScrollDownButton>;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
const SelectContent = t0 => {
  const $ = _c(25);
  let children;
  let className;
  let container;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      container,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = container;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    container = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
  }
  const position = t1 === undefined ? "popper" : t1;
  const orderedContainer = useOrderedOverlayContainer();
  const t2 = container ?? orderedContainer ?? undefined;
  let t3;
  if ($[7] !== className) {
    t3 = cn(menuContentVariants(), "relative max-h-96 min-w-select-trigger-width", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SelectScrollUpButton />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = position === "popper" && "h-select-trigger-height w-full min-w-select-trigger-width";
  let t6;
  if ($[10] !== t5) {
    t6 = cn("p-0", t5);
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children || $[13] !== t6) {
    t7 = <SelectPrimitive.Viewport className={t6}>{children}</SelectPrimitive.Viewport>;
    $[12] = children;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SelectScrollDownButton />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== position || $[17] !== props || $[18] !== ref || $[19] !== t3 || $[20] !== t7) {
    t9 = <SelectPrimitive.Content ref={ref} className={t3} position={position} {...props}>{t4}{t7}{t8}</SelectPrimitive.Content>;
    $[16] = position;
    $[17] = props;
    $[18] = ref;
    $[19] = t3;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t2 || $[23] !== t9) {
    t10 = <SelectPrimitive.Portal container={t2}>{t9}</SelectPrimitive.Portal>;
    $[22] = t2;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
};
SelectContent.displayName = SelectPrimitive.Content.displayName;
const SelectLabel = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(menuLabelClassName, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <SelectPrimitive.Label ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
SelectLabel.displayName = SelectPrimitive.Label.displayName;
const SelectItem = t0 => {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn(menuItemVariants({
      indicator: true
    }), className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className={menuItemIndicatorClassName}><SelectPrimitive.ItemIndicator><Check /></SelectPrimitive.ItemIndicator></span>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children) {
    t3 = <SelectPrimitive.ItemText><span>{children}</span></SelectPrimitive.ItemText>;
    $[8] = children;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props || $[11] !== ref || $[12] !== t1 || $[13] !== t3) {
    t4 = <SelectPrimitive.Item ref={ref} className={t1} {...props}>{t2}{t3}</SelectPrimitive.Item>;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(menuSeparatorClassName, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <SelectPrimitive.Separator ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue };